'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import WorldwayNav from '@/components/WorldwayNav';
import { getJourneyBySlug, ALL_JOURNEYS } from '../../all-journeys/journeyIndex';
import JourneyBookingWidget from '@/components/JourneyBookingWidget';

interface Props {
  params: { slug: string };
}

export default function JourneyDetailPage({ params }: Props) {
  const journey = getJourneyBySlug(params.slug);
  const [activeTab, setActiveTab] = useState<'overview' | 'itinerary' | 'details' | 'practical'>('overview');
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Normalize gallery to always be {url, alt}[]
  const gallery: {url: string; alt: string;}[] = (journey?.gallery ?? []).map((item, i) =>
    typeof item === 'string'
      ? { url: item, alt: `${journey?.title ?? 'Journey'} photo ${i + 1}` }
      : item
  );

  // Normalize faqs to always be {q, a}[]
  const faqs: {q: string; a: string;}[] = (journey?.faqs ?? []).map((f) => ({
    q: f.q ?? f.question ?? '',
    a: f.a ?? f.answer ?? '',
  }));

  // Normalize relatedSlugs
  const relatedSlugs: string[] = journey?.relatedSlugs ?? journey?.relatedJourneys ?? [];

  if (!journey) {
    return (
      <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)] flex items-center justify-center">
        <WorldwayNav />
        <div className="text-center pt-20">
          <h1 className="text-2xl font-bold mb-4">Journey Not Found</h1>
          <Link href="/all-journeys" className="text-[var(--primary)] hover:underline">← Back to All Journeys</Link>
        </div>
      </div>
    );
  }

  const relatedJourneys = ALL_JOURNEYS.filter((j) => relatedSlugs.includes(j.slug)).slice(0, 3);

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <WorldwayNav />

      {/* Hero */}
      <section className="relative pt-16 h-[70vh] min-h-[500px] overflow-hidden">
        <img
          src={gallery[galleryIndex]?.url ?? journey.image}
          alt={gallery[galleryIndex]?.alt ?? journey.alt}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        {/* Gallery dots */}
        {gallery.length > 1 && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2">
            {gallery.map((_, i) => (
              <button
                key={i}
                onClick={() => setGalleryIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === galleryIndex ? 'bg-[var(--primary)] w-6' : 'bg-white/50'}`}
              />
            ))}
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-white/60 mb-3">
            <Link href="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link href="/all-journeys" className="hover:text-white">All Journeys</Link>
            <span>/</span>
            <span className="text-white">{journey.region}</span>
          </nav>
          {journey.badge && (
            <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[var(--primary)] text-black mb-3">
              Worldway × A&K · {journey.badge}
            </span>
          )}
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'var(--font-serif)' }}>
            {journey.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
              {journey.days} days
            </span>
            <span className="flex items-center gap-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
              {journey.groupSize}
            </span>
            <span className="flex items-center gap-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
              {journey.activityLevel}
            </span>
            <span className="text-[var(--primary)] font-bold text-lg">{journey.priceLabel}</span>
          </div>
        </div>
      </section>

      {/* Gallery thumbnails */}
      <div className="bg-[var(--surface-2)] border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto">
          {gallery.map((img, i) => (
            <button
              key={i}
              onClick={() => setGalleryIndex(i)}
              className={`flex-shrink-0 w-20 h-14 rounded overflow-hidden border-2 transition-all ${i === galleryIndex ? 'border-[var(--primary)]' : 'border-transparent opacity-60 hover:opacity-100'}`}
            >
              <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 bg-[var(--background)] border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6 flex gap-0 overflow-x-auto">
          {(['overview', 'itinerary', 'details', 'practical'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 transition-all whitespace-nowrap ${activeTab === tab ? 'border-[var(--primary)] text-[var(--primary)]' : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]'}`}
            >
              {tab === 'overview' ? 'Overview' : tab === 'itinerary' ? 'Day by Day' : tab === 'details' ? 'Inclusions' : 'Practical Info'}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2">

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-2">Journey Overview</p>
                  <p className="text-sm text-[var(--muted-foreground)] leading-relaxed">{journey.overview}</p>
                </div>

                {/* Highlights */}
                <div>
                  <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>Journey Highlights</h2>
                  <ul className="space-y-2">
                    {(journey.highlights ?? []).map((h, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-[var(--muted-foreground)]">
                        <span className="w-5 h-5 rounded-full bg-[rgba(201,168,76,0.15)] text-[var(--primary)] flex items-center justify-center flex-shrink-0 text-[10px] font-bold mt-0.5">{i + 1}</span>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Itinerary route */}
                <div>
                  <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>Route</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    {(journey.itinerary ?? []).map((city, i) => (
                      <React.Fragment key={i}>
                        <span className="text-xs font-medium px-2 py-1 rounded bg-[var(--surface-3)] text-[var(--foreground)]">{city}</span>
                        {i < (journey.itinerary ?? []).length - 1 && <span className="text-[var(--primary)] text-xs">→</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Interests */}
                <div>
                  <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>Interests</h2>
                  <div className="flex flex-wrap gap-2">
                    {(journey.interests ?? []).map((interest) => (
                      <span key={interest} className="text-xs px-3 py-1 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)] border border-[rgba(201,168,76,0.2)]">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ITINERARY TAB */}
            {activeTab === 'itinerary' && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold mb-6" style={{ fontFamily: 'var(--font-serif)' }}>Day-by-Day Itinerary</h2>
                {(journey.itineraryDays ?? []).map((day, i) => (
                  <div key={i} className="border border-[var(--border)] rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-[var(--primary)] text-center leading-tight">{day.day.replace('Day ', 'D').replace('Days ', 'D')}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-sm mb-1">{day.title}</h3>
                        <p className="text-xs text-[var(--muted-foreground)] leading-relaxed mb-3">{day.description}</p>
                        <div className="flex flex-wrap gap-3 text-[10px] text-[var(--muted-foreground)]">
                          <span className="flex items-center gap-1">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                            {day.accommodation}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1" /><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" /><line x1="6" y1="1" x2="6" y2="4" /><line x1="10" y1="1" x2="10" y2="4" /><line x1="14" y1="1" x2="14" y2="4" /></svg>
                            {day.meals}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* DETAILS TAB */}
            {activeTab === 'details' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h2 className="text-base font-bold mb-3 text-emerald-400" style={{ fontFamily: 'var(--font-serif)' }}>✓ Included</h2>
                    <ul className="space-y-1.5">
                      {(journey.included ?? []).map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-[var(--muted-foreground)]">
                          <span className="text-emerald-400 mt-0.5">✓</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h2 className="text-base font-bold mb-3 text-rose-400" style={{ fontFamily: 'var(--font-serif)' }}>✗ Excluded</h2>
                    <ul className="space-y-1.5">
                      {(journey.excluded ?? []).map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-[var(--muted-foreground)]">
                          <span className="text-rose-400 mt-0.5">✗</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-base font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Accommodation</h2>
                  <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{journey.accommodation}</p>
                </div>

                <div>
                  <h2 className="text-base font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Transport</h2>
                  <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{journey.transport}</p>
                </div>

                <div>
                  <h2 className="text-base font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Meals</h2>
                  <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{journey.meals}</p>
                </div>

                <div>
                  <h2 className="text-base font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>Booking Conditions</h2>
                  <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{journey.bookingConditions}</p>
                </div>

                <div>
                  <h2 className="text-base font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>Cancellation Policy</h2>
                  <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{journey.cancellationPolicy}</p>
                </div>
              </div>
            )}

            {/* PRACTICAL TAB */}
            {activeTab === 'practical' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border border-[var(--border)] rounded-xl p-5">
                    <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                      <span className="text-[var(--primary)]">🌤</span> Weather
                    </h3>
                    <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{journey.weather}</p>
                  </div>
                  <div className="border border-[var(--border)] rounded-xl p-5">
                    <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                      <span className="text-[var(--primary)]">🛂</span> Visa
                    </h3>
                    <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{journey.visa}</p>
                  </div>
                </div>

                <div className="border border-[var(--border)] rounded-xl p-5">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                    <span className="text-[var(--primary)]">🎒</span> Packing Guide
                  </h3>
                  <ul className="grid grid-cols-2 gap-1.5">
                    {(journey.packing ?? []).map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-base font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>Frequently Asked Questions</h2>
                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="border border-[var(--border)] rounded-xl p-4">
                        <h4 className="font-semibold text-sm mb-2">{faq.q}</h4>
                        <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-base font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>Best Travel Months</h2>
                  <div className="flex flex-wrap gap-2">
                    {(journey.months ?? []).map((month) => (
                      <span key={month} className="text-xs px-3 py-1 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)] border border-[rgba(201,168,76,0.2)]">
                        {month}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Booking Widget */}
            <JourneyBookingWidget journey={journey} />

            {/* Cities */}
            <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-5">
              <h3 className="text-xs font-bold uppercase tracking-wider mb-3">Destinations</h3>
              <div className="flex flex-wrap gap-1.5">
                {(journey.cities ?? []).map((city) => (
                  <span key={city} className="text-[10px] px-2 py-1 rounded bg-[var(--surface-3)] text-[var(--muted-foreground)]">{city}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Similar Journeys */}
        {relatedJourneys.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'var(--font-serif)' }}>Similar Journeys</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedJourneys.map((related) => (
                <Link key={related.slug} href={`/journeys/${related.slug}`} className="group block bg-[var(--surface-2)] border border-[var(--border)] rounded-xl overflow-hidden hover:border-[rgba(201,168,76,0.35)] transition-all">
                  <div className="h-40 overflow-hidden">
                    <img src={related.image} alt={related.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-serif)' }}>{related.title}</h3>
                    <div className="flex items-center justify-between text-xs text-[var(--muted-foreground)]">
                      <span>{related.days} days</span>
                      <span className="text-[var(--primary)] font-bold">{related.priceLabel}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-[var(--border)] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 WorldwayLuxe · Worldway × A&K curated collection</div>
          <Link href="/all-journeys" className="text-xs text-[var(--primary)] hover:underline">← Back to All Journeys</Link>
        </div>
      </footer>
    </div>
  );
}
